import { hdTransitAddDays, hdTransitDaysBetween } from './hd-transit-day-flow.builder';

/**
 * Venster-logica van de dag-flow (spec §2.6, optie C "venster + treadmill"):
 * de flow rendert een begrensde, aaneengesloten reeks dagen; wat ver buiten
 * beeld scrollt verdwijnt écht uit de DOM, en aan de kant waar de gebruiker
 * naartoe scrollt komt telkens één dag bij. Pure functies — de component
 * meet de DOM en voert de stappen uit (met scroll-compensatie), zodat de
 * beslisregels hier zonder browser testbaar zijn.
 */

/** Gerenderde dagen: aaneengesloten lokale kalenderdagen, grenzen inbegrepen. */
export interface HdTransitDayWindow {
  readonly firstKey: string;
  readonly lastKey: string;
}

/** Scroll-metrics van de dag-flow na een render. */
export interface HdTransitDayFlowMetrics {
  readonly scrollTop: number;
  readonly clientHeight: number;
  readonly scrollHeight: number;
  /** Gerenderde dagsecties in volgorde, met top/hoogte in de scroll-inhoud (px). */
  readonly days: readonly { readonly key: string; readonly top: number; readonly height: number }[];
}

export type HdTransitDayWindowAction = 'prepend' | 'append' | 'dropFirst' | 'dropLast';

/** Nooit minder dagen gerenderd: genoeg om ook rond een lange dag te blijven scrollen. */
export const HD_TRANSIT_DAY_WINDOW_MIN_DAYS = 3;
/**
 * Harde bovengrens, puur een vangnet: in een host met begrensde hoogte vullen
 * de buffers zich ruim vóór deze grens (ook met enkel trage planeten, ±70 px
 * per dag). BELANGRIJK: zonder deze grens zou een host zónder hoogtebegrenzing
 * (viewport = inhoud, dus beide buffers lijken altijd leeg) eindeloos dagen
 * blijven toevoegen. Zie de window-spec "harde bovengrens".
 */
export const HD_TRANSIT_DAY_WINDOW_MAX_DAYS = 60;
/**
 * Minimale buffer (× viewporthoogte) onder het beeld vóór er een dag bijkomt.
 * Onderaan groeien kan ook tijdens het scrollen (de positie verandert niet).
 */
const GROW_BELOW_FACTOR = 0.75;
/**
 * Boven het beeld een ruimere buffer: daar mag het venster enkel groeien als
 * de gebruiker stilstaat (zie `aboveChangesAllowed`), dus moet er genoeg
 * klaarstaan om een volgende (momentum-)scroll naar boven op te vangen.
 */
const GROW_ABOVE_FACTOR = 1.5;
/**
 * Een randdag verdwijnt pas als hij volledig verder dan dit (× viewporthoogte)
 * buiten beeld staat — telkens ruim boven de groei-buffer van die kant, zodat
 * een weggehaalde dag nooit meteen weer bijgeladen moet worden (geen
 * heen-en-weer-oscillatie).
 */
const DROP_BELOW_FACTOR = 1.5;
const DROP_ABOVE_FACTOR = 3;

/** Beginvenster rond een focusdag: één dag ervoor (terugscrollen), twee erna. */
export function hdTransitInitialDayWindow(focusKey: string): HdTransitDayWindow {
  return { firstKey: hdTransitAddDays(focusKey, -1), lastKey: hdTransitAddDays(focusKey, 2) };
}

/** Alle dagsleutels van het venster, oplopend. */
export function hdTransitDayWindowKeys(window: HdTransitDayWindow): string[] {
  const count = hdTransitDaysBetween(window.firstKey, window.lastKey) + 1;
  return Array.from({ length: Math.max(0, count) }, (_, index) => hdTransitAddDays(window.firstKey, index));
}

/**
 * De volgende venster-stap op basis van de gemeten scroll-staat; `null` =
 * in evenwicht. Eén stap tegelijk: de component rendert, compenseert de
 * scroll en meet opnieuw. Eerst groeien (de buffer vult de scrollrichting),
 * dan snoeien.
 *
 * `aboveChangesAllowed: false` (de gebruiker scrollt nog): enkel stappen
 * ónder het beeld — een dag boven het beeld toevoegen of weghalen vergt een
 * scroll-compensatie, en die botst met een lopende (iOS-momentum-)scroll.
 */
export function planHdTransitDayWindowStep(
  metrics: HdTransitDayFlowMetrics,
  window: HdTransitDayWindow,
  options: {
    readonly bounds?: { readonly minKey: string; readonly maxKey: string };
    readonly aboveChangesAllowed?: boolean;
  } = {},
): HdTransitDayWindowAction | null {
  const bounds = options.bounds;
  const aboveAllowed = options.aboveChangesAllowed ?? true;
  const viewport = metrics.clientHeight;
  if (viewport <= 0 || metrics.days.length === 0) return null;

  const canGrow = metrics.days.length < HD_TRANSIT_DAY_WINDOW_MAX_DAYS;
  const canPrepend = aboveAllowed && canGrow && (bounds == null || window.firstKey > bounds.minKey);
  const canAppend = canGrow && (bounds == null || window.lastKey < bounds.maxKey);
  if (canPrepend && metrics.scrollTop < viewport * GROW_ABOVE_FACTOR) return 'prepend';
  if (canAppend && metrics.scrollHeight - (metrics.scrollTop + viewport) < viewport * GROW_BELOW_FACTOR) return 'append';

  if (metrics.days.length <= HD_TRANSIT_DAY_WINDOW_MIN_DAYS) return null;
  const first = metrics.days[0];
  if (aboveAllowed && first.top + first.height < metrics.scrollTop - viewport * DROP_ABOVE_FACTOR) return 'dropFirst';
  const last = metrics.days[metrics.days.length - 1];
  if (last.top > metrics.scrollTop + viewport + viewport * DROP_BELOW_FACTOR) return 'dropLast';
  return null;
}

/** Past één stap toe: het venster groeit of krimpt met één dag aan één kant. */
export function applyHdTransitDayWindowStep(
  window: HdTransitDayWindow,
  action: HdTransitDayWindowAction,
): HdTransitDayWindow {
  switch (action) {
    case 'prepend':
      return { ...window, firstKey: hdTransitAddDays(window.firstKey, -1) };
    case 'append':
      return { ...window, lastKey: hdTransitAddDays(window.lastKey, 1) };
    case 'dropFirst':
      return { ...window, firstKey: hdTransitAddDays(window.firstKey, 1) };
    case 'dropLast':
      return { ...window, lastKey: hdTransitAddDays(window.lastKey, -1) };
  }
}

/** De focusdag: de dag die de bovenrand van het zichtbare gebied bevat. */
export function hdTransitFocusDayKey(metrics: HdTransitDayFlowMetrics): string | null {
  const hit = metrics.days.find(day => day.top + day.height > metrics.scrollTop);
  return hit?.key ?? metrics.days[metrics.days.length - 1]?.key ?? null;
}
